import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Space, Tag, Select, Modal, Typography, Popconfirm, Empty, Spin, Alert, notification,
} from 'antd';
import { PlusOutlined, ClockCircleOutlined, KeyOutlined } from '@ant-design/icons';
import {
  listUserPermissionGrants,
  grantUserPermission,
  revokeUserPermission,
  listPermissionModules,
  listModuleActions,
} from './rbacApi';

const { Text, Paragraph } = Typography;

// Relative, not absolute: expiry is compared against the database clock, and a
// timestamp built in the browser is wrong whenever the two disagree on
// timezone. The backend resolves these with DATE_ADD(NOW(), ...).
const DURATIONS = [
  { value: 0, label: 'No expiry' },
  { value: 60, label: '1 hour' },
  { value: 60 * 8, label: '8 hours' },
  { value: 60 * 24, label: '1 day' },
  { value: 60 * 24 * 7, label: '1 week' },
  { value: 60 * 24 * 30, label: '30 days' },
];

const formatExpiry = (value) => {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

/**
 * Permissions granted to one user directly, shown apart from role-derived
 * access so a grant never becomes invisible debt sitting behind a role badge.
 *
 * Granting picks from the permission catalog, restricted to catalogued actions
 * that actually have a permission — you cannot grant something that does not
 * exist yet.
 */
export default function UserPermissionGrants({ userType = 'staff', userId, canManage = false }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [moduleKey, setModuleKey] = useState(undefined);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [permission, setPermission] = useState(undefined);
  const [duration, setDuration] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const body = await listUserPermissionGrants(userType, userId);
      setGrants(body?.data?.grants || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not load granted permissions');
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }, [userType, userId]);

  useEffect(() => { load(); }, [load]);

  const openModal = async () => {
    setOpen(true);
    setModuleKey(undefined);
    setPermission(undefined);
    setActions([]);
    setDuration(0);
    try {
      const body = await listPermissionModules();
      setModules(body?.data?.modules || []);
    } catch {
      setModules([]);
    }
  };

  const chooseModule = async (key) => {
    setModuleKey(key);
    setPermission(undefined);
    setActionsLoading(true);
    try {
      // 'used' = catalogued actions that already have a permission. Granting
      // requires the permission to exist; creating one is a separate flow.
      const body = await listModuleActions(key, 'used');
      setActions(body?.data?.actions || []);
    } catch {
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const held = useMemo(() => new Set(grants.map((g) => g.permission)), [grants]);

  const submit = async () => {
    if (!permission) return;
    setSaving(true);
    try {
      const payload = { permission };
      if (duration > 0) payload.expires_in_minutes = duration;
      const body = await grantUserPermission(userType, userId, payload);
      if (body && Number(body.error) !== 0) {
        throw new Error(body.message || 'Could not grant that permission');
      }
      notification.success({ message: `Granted ${permission}`, placement: 'topLeft' });
      setOpen(false);
      await load();
    } catch (e) {
      notification.error({
        message: 'Could not grant permission',
        description: e?.response?.data?.message || e?.message,
        placement: 'topLeft',
      });
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (perm) => {
    try {
      const body = await revokeUserPermission(userType, userId, perm);
      if (body && Number(body.error) !== 0) {
        throw new Error(body.message || 'Could not revoke');
      }
      notification.success({ message: `Revoked ${perm}`, placement: 'topLeft' });
      await load();
    } catch (e) {
      notification.error({
        message: 'Could not revoke permission',
        description: e?.response?.data?.message || e?.message,
        placement: 'topLeft',
      });
    }
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          <KeyOutlined /> Granted Directly
        </Typography.Title>
        {canManage ? (
          <Button size="small" icon={<PlusOutlined />} onClick={openModal}>
            Grant permission
          </Button>
        ) : null}
      </Space>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Permissions given to this person individually, on top of their roles. Shared
        role configuration is not changed.
      </Paragraph>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} /> : null}

      {loading ? (
        <Spin size="small" />
      ) : grants.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No permissions granted directly"
        />
      ) : (
        <Space wrap>
          {grants.map((g) => (
            <Tag
              key={g.permission}
              color={g.expired ? 'default' : 'gold'}
              closable={canManage}
              onClose={(e) => { e.preventDefault(); }}
              closeIcon={
                canManage ? (
                  <Popconfirm
                    title="Revoke this permission?"
                    description="Only this person is affected."
                    okText="Revoke"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => revoke(g.permission)}
                  >
                    <span style={{ marginLeft: 4 }}>×</span>
                  </Popconfirm>
                ) : null
              }
            >
              {g.display_name || g.permission}
              {g.expires_at ? (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                  <ClockCircleOutlined />{' '}
                  {g.expired ? 'expired' : `until ${formatExpiry(g.expires_at)}`}
                </Text>
              ) : null}
            </Tag>
          ))}
        </Space>
      )}

      <Modal
        title="Grant a permission to this user"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText="Grant"
        okButtonProps={{ disabled: !permission }}
        confirmLoading={saving}
      >
        <Paragraph type="secondary">
          This gives one person one capability without changing any shared role.
        </Paragraph>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Module</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            placeholder="Choose a module"
            value={moduleKey}
            onChange={chooseModule}
            options={modules.map((m) => ({
              value: m.module_key,
              label: `${m.label} (${m.assigned_count} available)`,
              disabled: m.assigned_count === 0,
            }))}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Permission</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            placeholder={moduleKey ? 'Choose a permission' : 'Choose a module first'}
            disabled={!moduleKey}
            loading={actionsLoading}
            value={permission}
            onChange={setPermission}
            notFoundContent={actionsLoading ? 'Loading…' : 'No permissions in this module yet'}
            options={actions.map((a) => ({
              value: a.permission_name || a.permission_slug,
              label: `${a.label} — ${a.permission_slug}`,
              disabled: held.has(a.permission_name || a.permission_slug),
            }))}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Permissions this person already holds directly are greyed out.
          </Text>
        </div>

        <div>
          <Text strong>Expires</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            value={duration}
            onChange={setDuration}
            options={DURATIONS}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Measured on the server clock. An expired grant simply stops applying —
            nothing needs cleaning up.
          </Text>
        </div>
      </Modal>
    </div>
  );
}
